"use client"

import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Calendar, Clock, ArrowRight, ArrowLeft, Tag } from 'lucide-react'

interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string
  image_url: string
  category: string | null
  tags: string[]
  featured: boolean
  created_at: string
  reading_time_minutes: number
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function formatTagName(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function BlogCardSkeleton() {
  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white shadow-sm">
      <Skeleton className="aspect-[4/5] w-full" />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <Skeleton className="h-4 w-20 mb-3" />
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  )
}

function BlogCard({ post, index }: { post: BlogPost; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="group"
    >
      <Link 
        to={`/blog/${post.slug}`}
        className="block relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-500"
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={post.image_url}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
          
          {post.featured && (
            <div className="absolute top-4 left-4">
              <Badge className="bg-[#DBB981] text-[#231f20] hover:bg-[#DBB981]/90 font-medium">
                Featured
              </Badge>
            </div>
          )}
          
          {post.category && (
            <div className="absolute top-4 right-4">
              <Badge variant="secondary" className="bg-white/90 text-[rgb(132,137,116)] backdrop-blur-sm">
                {post.category}
              </Badge>
            </div>
          )}
          
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center gap-4 text-sm text-white/80 mb-3">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {post.reading_time_minutes} min read
              </span>
            </div>
            
            <h2 className="text-xl font-medium leading-snug mb-2 group-hover:text-[#DBB981] transition-colors duration-300">
              {post.title}
            </h2>
            
            <p className="text-sm text-white/80 line-clamp-2 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
              {post.excerpt}
            </p>
            
            <div className="flex items-center gap-2 mt-3 text-[#DBB981] opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300 delay-100">
              <span className="text-sm font-medium">Read Article</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}

export default function BlogTagPage() {
  const { tag } = useParams<{ tag: string }>()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [originalTag, setOriginalTag] = useState<string>('')

  const tagName = tag ? formatTagName(tag) : ''

  useEffect(() => {
    async function fetchPosts() {
      if (!tag) return
      
      setIsLoading(true)
      
      try {
        // Fetch all published posts that have the tag
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('published', true)
          .order('created_at', { ascending: false })
        
        if (!error && data) {
          // Filter posts that contain the tag (case-insensitive match)
          const filteredPosts = data.filter(post => {
            if (!post.tags || !Array.isArray(post.tags)) return false
            return post.tags.some(t => {
              const tagSlug = t.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
              return tagSlug === tag.toLowerCase()
            })
          })
          
          // Find the original tag name
          if (filteredPosts.length > 0 && filteredPosts[0].tags) {
            const matchingTag = filteredPosts[0].tags.find(t => {
              const tagSlug = t.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
              return tagSlug === tag.toLowerCase()
            })
            if (matchingTag) {
              setOriginalTag(matchingTag)
            }
          }
          
          setPosts(filteredPosts)
        }
      } catch (err) {
        console.error('Error fetching posts:', err)
      }
      
      setIsLoading(false)
    }

    fetchPosts()
  }, [tag])

  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>#{originalTag || tagName} | Raintree Chinese Medicine Blog</title>
        <meta name="description" content={`Articles tagged with "${originalTag || tagName}" from Raintree Chinese Medicine. Expert insights on acupuncture, herbal medicine, and holistic health.`} />
        <meta property="og:title" content={`#${originalTag || tagName} | Raintree Blog`} />
        <meta property="og:description" content={`Articles tagged with "${originalTag || tagName}" from Raintree Chinese Medicine.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://raintreechinesemedicine.com.au/blog/tag/${tag}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`#${originalTag || tagName} | Raintree Blog`} />
        <meta name="twitter:description" content={`Articles tagged with "${originalTag || tagName}" from Raintree Chinese Medicine.`} />
      </Helmet>
      <h1 className="sr-only">Articles tagged: {originalTag || tagName}</h1>
      <BreadcrumbsJsonLd />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[rgb(132,137,116)]/5 to-transparent">
        <div className="container mx-auto px-4">
          {/* Back Link */}
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to All Articles</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <Tag className="w-5 h-5 text-[rgb(132,137,116)]" />
              <Badge variant="secondary" className="bg-gray-100 text-gray-600">Tag</Badge>
            </div>
            <h1 className="text-5xl md:text-6xl font-light mb-6 text-[rgb(132,137,116)] tracking-tight">
              #{originalTag || tagName}
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              {posts.length} {posts.length === 1 ? 'article' : 'articles'} tagged with "{originalTag || tagName}"
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[...Array(6)].map((_, i) => (
                <BlogCardSkeleton key={i} />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-4">🍃</div>
              <h3 className="text-xl text-gray-600 mb-2">No articles found with this tag</h3>
              <p className="text-gray-500 mb-6">Try searching for something else or explore all articles.</p>
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 text-[rgb(132,137,116)] hover:text-[#DBB981] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {posts.map((post, index) => (
                  <BlogCard key={post.id} post={post} index={index} />
                ))}
              </div>
            </AnimatePresence>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[rgb(132,137,116)]">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-light text-white mb-4">
              Explore More Topics
            </h2>
            <p className="text-white/80 mb-8">
              Discover more insights on Chinese medicine and holistic health.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[rgb(132,137,116)] rounded-full font-medium hover:bg-[#DBB981] transition-colors duration-300"
            >
              View All Articles
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  )
}

